/** The token could not be acquired and no interaction could be started. */
export class TokenAcquisitionError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('Could not acquire an access token.');
    this.name = 'TokenAcquisitionError';
    this.cause = cause;
  }
}

/**
 * Wrong password, or an email with no account. Deliberately the same error for
 * both: with email-enumeration protection on, Firebase already returns
 * `auth/invalid-credential` for either case, so distinguishing them in the UI
 * is both impossible and undesirable. See firebaseErrors.ts.
 */
export class InvalidCredentialsError extends Error {
  constructor() {
    super('Invalid email or password.');
    this.name = 'InvalidCredentialsError';
  }
}

/**
 * The Firebase user is disabled — by B5's deactivation step, or by admin
 * action in the console. Terminal, and the same situation as an
 * access-not-provisioned 401 reached by the other route: the caller routes
 * here to `/access-not-provisioned`, which already has no retry and no
 * sign-out.
 */
export class AccountDisabledError extends Error {
  constructor() {
    super('This account has been disabled.');
    this.name = 'AccountDisabledError';
  }
}

/** Firebase's own throttling after repeated failed sign-in attempts. */
export class TooManyAttemptsError extends Error {
  constructor() {
    super('Too many attempts. Wait a while, or reset your password.');
    this.name = 'TooManyAttemptsError';
  }
}

/** Offline, or the request to Firebase was blocked. Retryable. */
export class AuthNetworkError extends Error {
  constructor() {
    super('Could not reach the sign-in service.');
    this.name = 'AuthNetworkError';
  }
}

/**
 * Anything else. `cause` carries the original error for logging, but its
 * message must never reach a user: Firebase's own messages are
 * developer-facing and occasionally leak account-state detail.
 */
export class AuthUnavailableError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('Sign-in is temporarily unavailable.');
    this.name = 'AuthUnavailableError';
    this.cause = cause;
  }
}

/** A TOTP or SMS code was wrong or has expired. Retry in place. */
export class InvalidSecondFactorCodeError extends Error {
  constructor() {
    super("That code didn't work.");
    this.name = 'InvalidSecondFactorCodeError';
  }
}

/**
 * The provider only allows this change shortly after sign-in. The caller asks
 * for the password again (AuthProvider.reauthenticate), then resumes.
 */
export class RecentSignInRequiredError extends Error {
  constructor() {
    super('Confirm your password to continue.');
    this.name = 'RecentSignInRequiredError';
  }
}

/** Second-factor enrollment needs a verified email address first. */
export class EmailNotVerifiedError extends Error {
  constructor() {
    super('Verify your email address before setting up a second factor.');
    this.name = 'EmailNotVerifiedError';
  }
}

export class TooManySecondFactorsError extends Error {
  constructor() {
    super('This account already has the maximum number of second factors.');
    this.name = 'TooManySecondFactorsError';
  }
}

export class SecondFactorInUseError extends Error {
  constructor() {
    super('That second factor is already enrolled.');
    this.name = 'SecondFactorInUseError';
  }
}

export class InvalidPhoneNumberError extends Error {
  constructor() {
    super('That phone number is not valid.');
    this.name = 'InvalidPhoneNumberError';
  }
}
